import { apiSlice } from '@/store/api/apiSlice'

/**
 * A run's lifecycle status. Derived by the backend from its rows on every request
 * rather than stored, except `needs_reupload`, which records a decision taken once
 * at completion.
 */
export type BulkImportRunStatus =
  'pending' | 'running' | 'complete' | 'needs_reupload'

/** What the upload endpoint answers with. Outcomes are not among them: at the moment
 *  the response is written the rows have been accepted and dispatched, not processed. */
export interface BulkImportRunCreated {
  id: number
  account_id: number
  total_rows: number
  /**
   * True when these exact bytes were already submitted for this account inside the
   * backend's idempotency window, so this request returned the run that already
   * existed instead of starting a second import. A caller that meant to import the
   * file again should read it as its request not having taken effect.
   */
  replayed: boolean
}

/**
 * Live progress for one run. Every number is counted from the rows when asked for.
 * Render these as they arrive — do not cache them, and do not recompute any of them
 * from the row list. A tally kept in step with reality by hand is the defect this
 * whole feature was designed against (DM-5917).
 */
export interface BulkImportRunProgress {
  id: number
  status: BulkImportRunStatus
  /** False while any row can still change on its own, whatever the counts look like. */
  is_complete: boolean
  total_rows: number
  created_count: number
  /** Rows that failed and are the admin's to act on. */
  failed_count: number
  /** Rows still moving on their own, which need nothing from anyone. */
  in_progress_count: number
  counts_by_status: Record<string, number>
}

/**
 * One past run in an account's import history, newest first.
 *
 * `created_count` counts people created by identity link and overlaps the other counts,
 * so it is **not** a success count: succeeded is total − failed − in progress.
 */
export interface BulkImportRunSummary {
  id: number
  created_at: string
  status: BulkImportRunStatus
  /** The email of whoever started the run — the admin, or the staff member. */
  initiated_by: string
  /** True when Lytho staff ran it on the account's behalf. */
  initiated_by_is_staff: boolean
  total_count: number
  failed_count: number
  in_progress_count: number
  created_count: number
}

export interface GetBulkImportRunsArgs {
  accountId: number
  limit: number
  offset: number
}

/**
 * A row's own lifecycle. Only `error` can be retried; `needs_support` has spent its
 * attempts, and the five before `ready` are moving on their own and need nothing.
 */
export type BulkImportRowStatus =
  | 'pending'
  | 'processing'
  | 'creating'
  | 'provisioning'
  | 'provisioning_apps'
  | 'ready'
  | 'error'
  | 'needs_support'

/**
 * Where each leg of one row got to. Null where that leg has not been reached.
 *
 * Shown per integration rather than as one verdict because "the person exists, their
 * Workflow access does not" is a different problem, with a different fix, from "the
 * person was never created".
 */
export interface BulkImportRowIntegrations {
  identity: string | null
  dam: string | null
  workflow: string | null
}

/** One row of a run that still needs an admin to do something about it. */
export interface BulkImportRow {
  id: number
  /** The line in the file the admin uploaded, counting the header. */
  source_line_number: number
  email: string
  status: BulkImportRowStatus
  /** The most recent failure. Blank while the row has not failed. */
  error_message: string
  retry_count: number
  integrations: BulkImportRowIntegrations
}

/** What a corrected failure report did when it was sent back against its run. */
export interface BulkImportRecoveryResult {
  /** Rows the file sent back to be retried. */
  retried: number
  /** Rows in the file with no attempts left, now with support. */
  escalated: number
  /** Rows that stopped being retryable between being read and being acted on. */
  skipped: number
  /**
   * Rows whose corrections were not applied — the person already exists, so retrying
   * re-attempts their access without reading the file's values, or the row is with
   * support and nothing will read them again. Editing someone who already exists is a
   * user change, not an import.
   */
  details_not_applied: number
}

export interface CreateBulkImportArgs {
  accountId: number
  file: File
  suppressInvite: boolean
}

/**
 * The account import history's cache tag. Run-scoped invalidations (`{ id: runId }`) do
 * not reach a query that provides only the bare type, so the history needs its own id
 * for every action that changes a run's status or counts to invalidate.
 */
const BULK_IMPORT_HISTORY_TAG = { type: 'BulkImport' as const, id: 'LIST' }

export const bulkImportApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    createBulkImport: builder.mutation<
      BulkImportRunCreated,
      CreateBulkImportArgs
    >({
      query: ({ accountId, file, suppressInvite }) => {
        // Multipart, not JSON, and not by preference: the backend restricts this route
        // to a multipart parser so the import's own 5 MB ceiling governs the upload.
        // The same CSV in a JSON body would be refused by Django at its unset 2.5 MB
        // default, against a limit nothing in either codebase states.
        const body = new FormData()
        body.append('file', file)
        body.append('suppress_invite', String(suppressInvite))
        return {
          url: `/public/v1/accounts/${accountId}/bulk-imports/`,
          method: 'POST',
          body,
        }
      },
      // The account's user list changes as the run provisions, not when it is accepted,
      // but the run itself is new and anything listing runs should see it.
      invalidatesTags: ['BulkImport'],
    }),
    getBulkImportRows: builder.query<BulkImportRow[], number>({
      // Only the rows needing attention, in file order. A row that produced a working
      // user is deliberately absent — it is represented in the counts, and burying four
      // failures among five hundred successes is how the four get missed.
      query: runId => `/public/v1/bulk-imports/${runId}/rows/`,
      providesTags: (_result, _error, runId) => [
        { type: 'BulkImport', id: runId },
      ],
    }),
    dispatchBulkImportRun: builder.mutation<{ id: number }, number>({
      // The way out of a run whose one dispatch message was lost: its rows sit PENDING,
      // nothing failed so retry finds nothing to do, and re-uploading the file is
      // recognised as a resend. Answers 202 — the rows moving is the only confirmation.
      query: runId => ({
        url: `/public/v1/bulk-imports/${runId}/dispatch/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, runId) => [
        { type: 'BulkImport', id: runId },
        BULK_IMPORT_HISTORY_TAG,
      ],
    }),
    retryBulkImportRow: builder.mutation<
      Pick<BulkImportRow, 'id' | 'status' | 'retry_count' | 'error_message'>,
      { runId: number; rowId: number }
    >({
      // Synchronous, and answers with where the row is now. A row that is no longer
      // retryable answers 409 rather than a quiet 200 — the admin clicked a button on a
      // list that was true when it was drawn.
      query: ({ runId, rowId }) => ({
        url: `/public/v1/bulk-imports/${runId}/rows/${rowId}/retry/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { runId }) => [
        { type: 'BulkImport', id: runId },
        BULK_IMPORT_HISTORY_TAG,
      ],
    }),
    retryBulkImportFailures: builder.mutation<{ id: number }, number>({
      // Accepted, not done: the work moved to a worker, so this answers 202 with the run
      // id and nothing else. What each retry did is read by polling the run.
      query: runId => ({
        url: `/public/v1/bulk-imports/${runId}/retry/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, runId) => [
        { type: 'BulkImport', id: runId },
        BULK_IMPORT_HISTORY_TAG,
      ],
    }),
    recoverBulkImportRun: builder.mutation<
      BulkImportRecoveryResult,
      { runId: number; file: File }
    >({
      // The corrected report, posted to the run being recovered rather than starting a
      // second one: a corrected file describes the same import, and a new run would
      // create the rows that already worked all over again.
      query: ({ runId, file }) => {
        const body = new FormData()
        body.append('file', file)
        return {
          url: `/public/v1/bulk-imports/${runId}/recover/`,
          method: 'POST',
          body,
        }
      },
      invalidatesTags: (_result, _error, { runId }) => [
        { type: 'BulkImport', id: runId },
        BULK_IMPORT_HISTORY_TAG,
      ],
    }),
    getBulkImportProgress: builder.query<BulkImportRunProgress, number>({
      query: runId => `/public/v1/bulk-imports/${runId}/`,
      providesTags: (_result, _error, runId) => [
        { type: 'BulkImport', id: runId },
      ],
    }),
    getBulkImportRuns: builder.query<
      BulkImportRunSummary[],
      GetBulkImportRunsArgs
    >({
      // A bounded window, newest first. A short page is the end of the history: the
      // endpoint returns no total. A new upload invalidates the bare type, which reaches
      // this; retry, dispatch and recovery change a run's status and counts too, and
      // invalidate this list tag beside their own run's.
      query: ({ accountId, limit, offset }) =>
        `/public/v1/accounts/${accountId}/bulk-imports/list/?limit=${limit}&offset=${offset}`,
      providesTags: [BULK_IMPORT_HISTORY_TAG],
    }),
  }),
})

export const {
  useCreateBulkImportMutation,
  useGetBulkImportProgressQuery,
  useGetBulkImportRowsQuery,
  useGetBulkImportRunsQuery,
  useDispatchBulkImportRunMutation,
  useRetryBulkImportRowMutation,
  useRetryBulkImportFailuresMutation,
  useRecoverBulkImportRunMutation,
} = bulkImportApi
